/**
 * Acid Reactions Lab (Year 9): reviewed reactions of dilute hydrochloric, sulfuric and nitric acid
 * with metals, carbonates, a hydrogencarbonate, a hydroxide and metal oxides. Every answer comes
 * from this typed data; unsupported combinations fail closed with a reason.
 */
import { indicatorBandFor, type IndicatorBand } from './pHScale';
import { activityRank, formatTerms, type EquationTerm } from './reactionPredictionData';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const ACID_REACTIONS_TOOL_IDENTITY = {
  id: 'acid-reactions',
  toolKey: 'chemistry-acid-reactions',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const ACID_REACTIONS_START_YEAR = 9;

export interface AcidReactionsProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const ACID_REACTIONS_PROVENANCE: AcidReactionsProvenance = {
  sources: [
    'IUPAC Nomenclature of Inorganic Chemistry (Red Book, 2005) — salt names: chloride, sulfate, nitrate',
    'Metal reactivity (activity) series consistent with standard electrode potentials (CRC Handbook of Chemistry and Physics)',
    'Standard school gas tests (hydrogen: squeaky pop with a lit splint; carbon dioxide: limewater turns milky) and indicator colour charts as recorded in common Australian Year 9–10 science texts',
  ],
  conditions:
    'Dilute acids (about 0.1–1 mol/L) at room temperature (about 25 °C) and 100 kPa. Copper(II) oxide needs gentle warming.',
  reviewStatus: 'pending-teaching-review',
};

// ---------------------------------------------------------------------------------------------
// Acids and partners
// ---------------------------------------------------------------------------------------------

export type AcidId = 'HCl' | 'H2SO4' | 'HNO3';
export type SaltFamily = 'chloride' | 'sulfate' | 'nitrate';

export interface AcidData {
  id: AcidId;
  name: string;
  /** Formula with state, for example `HCl(aq)`. */
  species: string;
  saltFamily: SaltFamily;
  anion: string;
}

export const ACIDS: AcidData[] = [
  { id: 'HCl', name: 'hydrochloric acid', species: 'HCl(aq)', saltFamily: 'chloride', anion: 'Cl⁻' },
  { id: 'H2SO4', name: 'sulfuric acid', species: 'H2SO4(aq)', saltFamily: 'sulfate', anion: 'SO₄²⁻' },
  { id: 'HNO3', name: 'nitric acid', species: 'HNO3(aq)', saltFamily: 'nitrate', anion: 'NO₃⁻' },
];

export type PartnerId = 'Mg' | 'Zn' | 'Fe' | 'Cu' | 'CaCO3' | 'Na2CO3' | 'NaHCO3' | 'NaOH' | 'CuO' | 'MgO';
export type PartnerKind = 'metal' | 'carbonate' | 'hydrogencarbonate' | 'hydroxide' | 'oxide';

export interface PartnerData {
  id: PartnerId;
  name: string;
  species: string;
  kind: PartnerKind;
  /** Cation name used in the salt, for example `iron(II)`. */
  cationName: string;
  appearance: string;
  /** pH of the mixture when this soluble partner is added in excess; absent for insoluble partners. */
  excessPH?: number;
}

export const PARTNERS: PartnerData[] = [
  { id: 'Mg', name: 'magnesium', species: 'Mg(s)', kind: 'metal', cationName: 'magnesium', appearance: 'grey ribbon' },
  { id: 'Zn', name: 'zinc', species: 'Zn(s)', kind: 'metal', cationName: 'zinc', appearance: 'grey granules' },
  { id: 'Fe', name: 'iron', species: 'Fe(s)', kind: 'metal', cationName: 'iron(II)', appearance: 'grey filings' },
  { id: 'Cu', name: 'copper', species: 'Cu(s)', kind: 'metal', cationName: 'copper(II)', appearance: 'pink-brown turnings' },
  { id: 'CaCO3', name: 'calcium carbonate', species: 'CaCO3(s)', kind: 'carbonate', cationName: 'calcium', appearance: 'white marble chips' },
  { id: 'Na2CO3', name: 'sodium carbonate', species: 'Na2CO3(s)', kind: 'carbonate', cationName: 'sodium', appearance: 'white powder', excessPH: 11 },
  { id: 'NaHCO3', name: 'sodium hydrogencarbonate', species: 'NaHCO3(s)', kind: 'hydrogencarbonate', cationName: 'sodium', appearance: 'white powder (baking soda)', excessPH: 8 },
  { id: 'NaOH', name: 'sodium hydroxide', species: 'NaOH(aq)', kind: 'hydroxide', cationName: 'sodium', appearance: 'colourless solution', excessPH: 13 },
  { id: 'CuO', name: 'copper(II) oxide', species: 'CuO(s)', kind: 'oxide', cationName: 'copper(II)', appearance: 'black powder' },
  { id: 'MgO', name: 'magnesium oxide', species: 'MgO(s)', kind: 'oxide', cationName: 'magnesium', appearance: 'white powder' },
];

export const PARTNER_KIND_LABELS: Record<PartnerKind, string> = {
  metal: 'Metals',
  carbonate: 'Carbonates',
  hydrogencarbonate: 'Hydrogencarbonate',
  hydroxide: 'Hydroxide (alkali)',
  oxide: 'Metal oxides (bases)',
};

export function acidById(id: AcidId): AcidData {
  const acid = ACIDS.find((entry) => entry.id === id);
  if (!acid) throw new Error(`Unknown acid: ${id}`);
  return acid;
}

export function partnerById(id: PartnerId): PartnerData {
  const partner = PARTNERS.find((entry) => entry.id === id);
  if (!partner) throw new Error(`Unknown partner: ${id}`);
  return partner;
}

// ---------------------------------------------------------------------------------------------
// Patterns, gas tests and the reviewed reactions
// ---------------------------------------------------------------------------------------------

export type ProductPattern = 'salt-hydrogen' | 'salt-water-carbon-dioxide' | 'salt-water' | 'no-reaction';
export type GasTestId = 'squeaky-pop' | 'limewater' | 'glowing-splint' | 'no-gas';
export type Vigour = 'none' | 'slow' | 'steady' | 'rapid';

export const PATTERN_LABELS: Record<ProductPattern, string> = {
  'salt-hydrogen': 'Salt + hydrogen',
  'salt-water-carbon-dioxide': 'Salt + water + carbon dioxide',
  'salt-water': 'Salt + water',
  'no-reaction': 'No reaction',
};

export const GAS_TEST_LABELS: Record<GasTestId, string> = {
  'squeaky-pop': 'Lit splint gives a squeaky pop',
  limewater: 'Limewater turns milky',
  'glowing-splint': 'Glowing splint relights',
  'no-gas': 'No gas to test',
};

export const GAS_TEST_DETAILS: Record<GasTestId, { gas: string; method: string; result: string }> = {
  'squeaky-pop': {
    gas: 'Hydrogen, H₂',
    method: 'Collect the gas in an upturned test tube and hold a lit splint at the mouth.',
    result: 'A squeaky pop: hydrogen burns quickly with oxygen in the air to form water.',
  },
  limewater: {
    gas: 'Carbon dioxide, CO₂',
    method: 'Bubble the gas through limewater (calcium hydroxide solution).',
    result: 'The limewater turns milky (cloudy) because insoluble calcium carbonate forms.',
  },
  'glowing-splint': {
    gas: 'Oxygen, O₂',
    method: 'Put a glowing splint into the gas.',
    result: 'The splint relights. No reaction in this tool makes oxygen.',
  },
  'no-gas': {
    gas: 'No gas',
    method: 'Nothing bubbles off, so there is no gas to collect.',
    result: 'Use temperature or an indicator to see that a reaction happened.',
  },
};

export const VIGOUR_LABELS: Record<Vigour, string> = {
  none: 'No bubbles',
  slow: 'Slow bubbling',
  steady: 'Steady bubbling',
  rapid: 'Rapid fizzing',
};

/** Bubble count drawn by the beaker schematic for each vigour level. */
export const VIGOUR_BUBBLES: Record<Vigour, number> = { none: 0, slow: 4, steady: 9, rapid: 16 };

export interface AcidReaction {
  id: string;
  acid: AcidId;
  partner: PartnerId;
  pattern: ProductPattern;
  reactants: EquationTerm[];
  products: EquationTerm[];
  /** Salt formula without state, for example `MgCl2`; null when there is no reaction. */
  saltFormula: string | null;
  vigour: Vigour;
  observations: string[];
  note?: string;
}

interface ReactionSpec {
  acid: AcidId;
  partner: PartnerId;
  saltFormula: string;
  /** Coefficients in order: partner, acid, then products (salt, then hydrogen or water and CO₂). */
  coefficients: number[];
  vigour: Vigour;
  observations: string[];
  note?: string;
}

const METAL_SALT_COLOURLESS = 'The solution stays colourless.';
const GETS_WARM = 'The test tube gets warm (exothermic).';

const REACTION_SPECS: ReactionSpec[] = [
  // Hydrochloric acid
  { acid: 'HCl', partner: 'Mg', saltFormula: 'MgCl2', coefficients: [1, 2, 1, 1], vigour: 'rapid', observations: ['Rapid fizzing on the ribbon.', 'The ribbon gets smaller and disappears.', GETS_WARM, METAL_SALT_COLOURLESS] },
  { acid: 'HCl', partner: 'Zn', saltFormula: 'ZnCl2', coefficients: [1, 2, 1, 1], vigour: 'steady', observations: ['Steady bubbling on the granules.', 'The zinc slowly gets smaller.', METAL_SALT_COLOURLESS] },
  { acid: 'HCl', partner: 'Fe', saltFormula: 'FeCl2', coefficients: [1, 2, 1, 1], vigour: 'slow', observations: ['Slow bubbling on the filings.', 'The solution slowly turns pale green (iron(II) ions).'] },
  { acid: 'HCl', partner: 'CaCO3', saltFormula: 'CaCl2', coefficients: [1, 2, 1, 1, 1], vigour: 'rapid', observations: ['Fizzing on the chips.', 'The chips get smaller.', METAL_SALT_COLOURLESS] },
  { acid: 'HCl', partner: 'Na2CO3', saltFormula: 'NaCl', coefficients: [1, 2, 2, 1, 1], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', METAL_SALT_COLOURLESS] },
  { acid: 'HCl', partner: 'NaHCO3', saltFormula: 'NaCl', coefficients: [1, 1, 1, 1, 1], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', 'The mixture gets slightly cooler (endothermic).'] },
  { acid: 'HCl', partner: 'NaOH', saltFormula: 'NaCl', coefficients: [1, 1, 1, 1], vigour: 'none', observations: ['No bubbles and no colour change: both solutions are colourless.', GETS_WARM, 'Only an indicator shows that the acid has been neutralised.'] },
  { acid: 'HCl', partner: 'CuO', saltFormula: 'CuCl2', coefficients: [1, 2, 1, 1], vigour: 'none', observations: ['On gentle warming the black powder dissolves.', 'The solution turns blue-green (copper(II) ions).', 'No bubbles.'] },
  { acid: 'HCl', partner: 'MgO', saltFormula: 'MgCl2', coefficients: [1, 2, 1, 1], vigour: 'none', observations: ['The white powder dissolves.', GETS_WARM, 'No bubbles.'] },
  // Sulfuric acid
  { acid: 'H2SO4', partner: 'Mg', saltFormula: 'MgSO4', coefficients: [1, 1, 1, 1], vigour: 'rapid', observations: ['Rapid fizzing on the ribbon.', 'The ribbon gets smaller and disappears.', GETS_WARM, METAL_SALT_COLOURLESS] },
  { acid: 'H2SO4', partner: 'Zn', saltFormula: 'ZnSO4', coefficients: [1, 1, 1, 1], vigour: 'steady', observations: ['Steady bubbling on the granules.', 'The zinc slowly gets smaller.', METAL_SALT_COLOURLESS] },
  { acid: 'H2SO4', partner: 'Fe', saltFormula: 'FeSO4', coefficients: [1, 1, 1, 1], vigour: 'slow', observations: ['Slow bubbling on the filings.', 'The solution slowly turns pale green (iron(II) ions).'] },
  { acid: 'H2SO4', partner: 'Na2CO3', saltFormula: 'Na2SO4', coefficients: [1, 1, 1, 1, 1], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', METAL_SALT_COLOURLESS] },
  { acid: 'H2SO4', partner: 'NaHCO3', saltFormula: 'Na2SO4', coefficients: [2, 1, 1, 2, 2], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', METAL_SALT_COLOURLESS] },
  { acid: 'H2SO4', partner: 'NaOH', saltFormula: 'Na2SO4', coefficients: [2, 1, 1, 2], vigour: 'none', observations: ['No bubbles and no colour change: both solutions are colourless.', GETS_WARM, 'Only an indicator shows that the acid has been neutralised.'] },
  { acid: 'H2SO4', partner: 'CuO', saltFormula: 'CuSO4', coefficients: [1, 1, 1, 1], vigour: 'none', observations: ['On gentle warming the black powder dissolves.', 'The solution turns blue (copper(II) ions).', 'No bubbles.'] },
  { acid: 'H2SO4', partner: 'MgO', saltFormula: 'MgSO4', coefficients: [1, 1, 1, 1], vigour: 'none', observations: ['The white powder dissolves.', GETS_WARM, 'No bubbles.'] },
  // Nitric acid (bases and carbonates only — see UNSUPPORTED for metals)
  { acid: 'HNO3', partner: 'CaCO3', saltFormula: 'Ca(NO3)2', coefficients: [1, 2, 1, 1, 1], vigour: 'rapid', observations: ['Fizzing on the chips.', 'The chips get smaller.', METAL_SALT_COLOURLESS] },
  { acid: 'HNO3', partner: 'Na2CO3', saltFormula: 'NaNO3', coefficients: [1, 2, 2, 1, 1], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', METAL_SALT_COLOURLESS] },
  { acid: 'HNO3', partner: 'NaHCO3', saltFormula: 'NaNO3', coefficients: [1, 1, 1, 1, 1], vigour: 'rapid', observations: ['Vigorous fizzing.', 'The white powder dissolves.', METAL_SALT_COLOURLESS] },
  { acid: 'HNO3', partner: 'NaOH', saltFormula: 'NaNO3', coefficients: [1, 1, 1, 1], vigour: 'none', observations: ['No bubbles and no colour change: both solutions are colourless.', GETS_WARM, 'Only an indicator shows that the acid has been neutralised.'] },
  { acid: 'HNO3', partner: 'CuO', saltFormula: 'Cu(NO3)2', coefficients: [1, 2, 1, 1], vigour: 'none', observations: ['On gentle warming the black powder dissolves.', 'The solution turns blue (copper(II) ions).', 'No bubbles.'] },
  { acid: 'HNO3', partner: 'MgO', saltFormula: 'Mg(NO3)2', coefficients: [1, 2, 1, 1], vigour: 'none', observations: ['The white powder dissolves.', GETS_WARM, 'No bubbles.'] },
];

const NO_REACTION_OBSERVATIONS = [
  'No bubbles, even after several minutes.',
  'The copper keeps its pink-brown colour and does not get smaller.',
  'The solution stays colourless and the temperature does not change.',
];

const NO_REACTION_NOTE =
  'Copper is below hydrogen in the activity series, so it cannot push hydrogen out of a dilute non-oxidising acid.';

/** Combinations this tool does not model. They fail closed with a reason. */
export const UNSUPPORTED_REASONS: Partial<Record<string, string>> = {
  'HNO3-Mg':
    'Nitric acid is an oxidising acid. With metals it usually makes nitrogen oxides instead of, or as well as, hydrogen, so the simple “acid + metal → salt + hydrogen” pattern does not apply. This tool does not model it.',
  'HNO3-Zn':
    'Nitric acid is an oxidising acid. With zinc it makes nitrogen oxides (and other products) rather than just hydrogen, so this tool does not model it.',
  'HNO3-Fe':
    'Nitric acid is an oxidising acid. Iron gives nitrogen oxides, and concentrated nitric acid can make iron passive, so this tool does not model it.',
  'HNO3-Cu':
    'Copper does react with nitric acid, even though it is below hydrogen: the acid acts as an oxidising agent and makes toxic nitrogen oxide gases. The activity series rule for hydrogen does not apply, so this tool does not model it.',
  'H2SO4-CaCO3':
    'Calcium sulfate is only slightly soluble. It coats the marble chips, so the fizzing slows and often stops before the acid is used up. The result depends on conditions, so this tool does not model it. Use hydrochloric or nitric acid with calcium carbonate.',
};

export const reactionId = (acid: AcidId, partner: PartnerId) => `${acid}-${partner}`;

function patternForKind(kind: PartnerKind): ProductPattern {
  if (kind === 'metal') return 'salt-hydrogen';
  if (kind === 'carbonate' || kind === 'hydrogencarbonate') return 'salt-water-carbon-dioxide';
  return 'salt-water';
}

/** Metals below hydrogen in the activity series do not react with dilute non-oxidising acids. */
export function isBelowHydrogen(partner: PartnerData): boolean {
  if (partner.kind !== 'metal') return false;
  const metalRank = activityRank(partner.id);
  const hydrogenRank = activityRank('H');
  if (metalRank < 0 || hydrogenRank < 0) throw new Error(`Metal not in activity series: ${partner.id}`);
  return metalRank > hydrogenRank;
}

function buildReaction(spec: ReactionSpec): AcidReaction {
  const acid = acidById(spec.acid);
  const partner = partnerById(spec.partner);
  const pattern = patternForKind(partner.kind);
  const productSpecies =
    pattern === 'salt-hydrogen'
      ? [`${spec.saltFormula}(aq)`, 'H2(g)']
      : pattern === 'salt-water-carbon-dioxide'
        ? [`${spec.saltFormula}(aq)`, 'H2O(l)', 'CO2(g)']
        : [`${spec.saltFormula}(aq)`, 'H2O(l)'];
  const species = [partner.species, acid.species, ...productSpecies];
  if (spec.coefficients.length !== species.length) {
    throw new Error(`Coefficient count mismatch for ${reactionId(spec.acid, spec.partner)}`);
  }
  const terms = species.map((id, index) => ({ coefficient: spec.coefficients[index], species: id }));
  return {
    id: reactionId(spec.acid, spec.partner),
    acid: spec.acid,
    partner: spec.partner,
    pattern,
    reactants: terms.slice(0, 2),
    products: terms.slice(2),
    saltFormula: spec.saltFormula,
    vigour: spec.vigour,
    observations: spec.observations,
    note: spec.note,
  };
}

function buildNoReaction(acid: AcidId, partner: PartnerId): AcidReaction {
  return {
    id: reactionId(acid, partner),
    acid,
    partner,
    pattern: 'no-reaction',
    reactants: [
      { coefficient: 1, species: partnerById(partner).species },
      { coefficient: 1, species: acidById(acid).species },
    ],
    products: [],
    saltFormula: null,
    vigour: 'none',
    observations: NO_REACTION_OBSERVATIONS,
    note: NO_REACTION_NOTE,
  };
}

export const ACID_REACTIONS: AcidReaction[] = [
  ...REACTION_SPECS.map(buildReaction),
  buildNoReaction('HCl', 'Cu'),
  buildNoReaction('H2SO4', 'Cu'),
];

export type ReactionLookup =
  | { status: 'reviewed'; reaction: AcidReaction }
  | { status: 'unsupported'; reason: string };

const UNKNOWN_REASON = 'This combination has not been reviewed, so the tool does not predict a result.';

export function lookupReaction(acid: AcidId, partner: PartnerId): ReactionLookup {
  const id = reactionId(acid, partner);
  const unsupported = UNSUPPORTED_REASONS[id];
  if (unsupported) return { status: 'unsupported', reason: unsupported };
  const reaction = ACID_REACTIONS.find((entry) => entry.id === id);
  return reaction ? { status: 'reviewed', reaction } : { status: 'unsupported', reason: UNKNOWN_REASON };
}

// ---------------------------------------------------------------------------------------------
// Names and equations
// ---------------------------------------------------------------------------------------------

export function saltName(reaction: AcidReaction): string | null {
  if (reaction.pattern === 'no-reaction') return null;
  return `${partnerById(reaction.partner).cationName} ${acidById(reaction.acid).saltFamily}`;
}

const PATTERN_PRODUCT_WORDS: Record<Exclude<ProductPattern, 'no-reaction'>, string[]> = {
  'salt-hydrogen': ['hydrogen'],
  'salt-water-carbon-dioxide': ['water', 'carbon dioxide'],
  'salt-water': ['water'],
};

export function wordEquation(reaction: AcidReaction): string {
  const partner = partnerById(reaction.partner).name;
  const acid = acidById(reaction.acid).name;
  if (reaction.pattern === 'no-reaction') return `${partner} + ${acid} → no reaction`;
  const salt = saltName(reaction);
  return `${partner} + ${acid} → ${[salt, ...PATTERN_PRODUCT_WORDS[reaction.pattern]].join(' + ')}`;
}

export function balancedEquation(reaction: AcidReaction): string {
  if (reaction.pattern === 'no-reaction') return `${formatTerms(reaction.reactants)} → no reaction`;
  return `${formatTerms(reaction.reactants)} → ${formatTerms(reaction.products)}`;
}

export const SALT_NAMING_RULES: Record<SaltFamily, string> = {
  chloride: 'Hydrochloric acid makes chlorides (Cl⁻).',
  sulfate: 'Sulfuric acid makes sulfates (SO₄²⁻).',
  nitrate: 'Nitric acid makes nitrates (NO₃⁻).',
};

export function gasTestFor(pattern: ProductPattern): GasTestId {
  if (pattern === 'salt-hydrogen') return 'squeaky-pop';
  if (pattern === 'salt-water-carbon-dioxide') return 'limewater';
  return 'no-gas';
}

// ---------------------------------------------------------------------------------------------
// Predictions and feedback
// ---------------------------------------------------------------------------------------------

export interface PredictionFeedback {
  correct: boolean;
  feedback: string;
}

export const PATTERN_OPTIONS = Object.keys(PATTERN_LABELS) as ProductPattern[];
export const GAS_TEST_OPTIONS = Object.keys(GAS_TEST_LABELS) as GasTestId[];

function correctPatternFeedback(reaction: AcidReaction, partner: PartnerData): string {
  switch (reaction.pattern) {
    case 'salt-hydrogen':
      return `Correct. ${capitalise(partner.name)} is above hydrogen in the activity series, so it takes the place of hydrogen in the acid: a salt forms and hydrogen gas bubbles off.`;
    case 'salt-water-carbon-dioxide':
      return `Correct. Acid breaks down the ${partner.kind === 'hydrogencarbonate' ? 'hydrogencarbonate (HCO₃⁻)' : 'carbonate (CO₃²⁻)'}: a salt, water and carbon dioxide form.`;
    case 'salt-water':
      return `Correct. ${capitalise(partner.name)} is a base, so it neutralises the acid: H⁺ from the acid and the ${partner.kind === 'oxide' ? 'oxide (O²⁻)' : 'hydroxide (OH⁻)'} make water, and a salt is left in solution.`;
    default:
      return 'Correct. Copper is below hydrogen in the activity series, so it cannot displace hydrogen from a dilute acid. Nothing happens.';
  }
}

function wrongPatternFeedback(reaction: AcidReaction, partner: PartnerData, choice: ProductPattern): string {
  if (reaction.pattern === 'no-reaction') {
    return 'Copper is below hydrogen in the activity series. A metal must be more reactive than hydrogen to displace it from an acid, so there is no reaction.';
  }
  if (choice === 'no-reaction') {
    return partner.kind === 'metal'
      ? `${capitalise(partner.name)} is above hydrogen in the activity series, so it does react with the acid.`
      : `Acids react with every ${partner.kind === 'carbonate' || partner.kind === 'hydrogencarbonate' ? 'carbonate and hydrogencarbonate' : 'base'} in this tool. ${capitalise(partner.name)} uses up the acid.`;
  }
  if (choice === 'salt-hydrogen') {
    return `Hydrogen gas forms when a reactive metal displaces hydrogen. ${capitalise(partner.name)} is not a metal element, so no hydrogen forms.`;
  }
  if (choice === 'salt-water-carbon-dioxide') {
    return `Carbon dioxide comes only from a carbonate or hydrogencarbonate. ${capitalise(partner.name)} contains no carbon, so there is no CO₂.`;
  }
  return partner.kind === 'metal'
    ? 'Water forms when an acid reacts with a base (an oxide or hydroxide). A metal element has no oxygen to make water: it makes hydrogen gas instead.'
    : 'Close, but the carbonate or hydrogencarbonate also breaks down: carbon dioxide is released as well as water. That is why it fizzes.';
}

export function checkPatternPrediction(reaction: AcidReaction, choice: ProductPattern): PredictionFeedback {
  const partner = partnerById(reaction.partner);
  if (choice === reaction.pattern) return { correct: true, feedback: correctPatternFeedback(reaction, partner) };
  return { correct: false, feedback: wrongPatternFeedback(reaction, partner, choice) };
}

export function checkGasTestPrediction(reaction: AcidReaction, choice: GasTestId): PredictionFeedback {
  const expected = gasTestFor(reaction.pattern);
  if (choice === expected) {
    return { correct: true, feedback: `Correct. ${GAS_TEST_DETAILS[expected].result}` };
  }
  const feedback: Record<GasTestId, string> = {
    'squeaky-pop': 'A squeaky pop tests for hydrogen. Hydrogen only forms when a metal above hydrogen reacts with the acid.',
    limewater: 'Limewater tests for carbon dioxide. Carbon dioxide only forms from a carbonate or hydrogencarbonate.',
    'glowing-splint': 'A glowing splint tests for oxygen. None of these acid reactions makes oxygen.',
    'no-gas':
      expected === 'squeaky-pop'
        ? 'This reaction does release a gas: hydrogen. Test it with a lit splint.'
        : 'This reaction does release a gas: carbon dioxide. Test it with limewater.',
  };
  return { correct: false, feedback: feedback[choice] };
}

export type SaltOptionId = SaltFamily | 'sulfide';

export const SALT_OPTIONS: SaltOptionId[] = ['chloride', 'sulfate', 'nitrate', 'sulfide'];

export function saltOptionLabel(reaction: AcidReaction, option: SaltOptionId): string {
  return `${partnerById(reaction.partner).cationName} ${option}`;
}

export function checkSaltPrediction(reaction: AcidReaction, choice: SaltOptionId): PredictionFeedback | null {
  if (reaction.pattern === 'no-reaction') return null;
  const acid = acidById(reaction.acid);
  const name = saltName(reaction);
  if (choice === acid.saltFamily) {
    return { correct: true, feedback: `Correct: ${name}. ${SALT_NAMING_RULES[acid.saltFamily]} The metal part comes from ${partnerById(reaction.partner).name}.` };
  }
  if (choice === 'sulfide') {
    return {
      correct: false,
      feedback: 'A sulfide contains only sulfur (S²⁻). Sulfuric acid gives sulfates, which contain sulfur and oxygen (SO₄²⁻).',
    };
  }
  return {
    correct: false,
    feedback: `The salt name comes from the acid. ${SALT_NAMING_RULES[choice]} This reaction uses ${acid.name}, so the salt is ${name}.`,
  };
}

// ---------------------------------------------------------------------------------------------
// Indicators
// ---------------------------------------------------------------------------------------------

export type IndicatorStage = 'start' | 'used-up' | 'excess';

export const STAGE_LABELS: Record<IndicatorStage, string> = {
  start: 'Acid at the start',
  'used-up': 'Acid just used up',
  excess: 'Partner in excess',
};

/** Model pH of the dilute acid at the start (about 0.1 mol/L H⁺). */
export const START_PH = 1;
/** Model pH when the acid has just been used up (salt solution treated as neutral). */
export const NEUTRAL_SALT_PH = 7;
/** Phenolphthalein turns pink at about pH 8.2 and above. */
export const PHENOLPHTHALEIN_PINK_FROM_PH = 8.2;

export interface IndicatorReading {
  pH: number;
  litmus: string;
  universal: IndicatorBand;
  phenolphthalein: string;
}

export function indicatorReading(pH: number): IndicatorReading {
  const universal = indicatorBandFor(pH);
  const litmus = pH < NEUTRAL_SALT_PH ? 'Red' : pH === NEUTRAL_SALT_PH ? 'Purple (no change)' : 'Blue';
  const phenolphthalein = pH >= PHENOLPHTHALEIN_PINK_FROM_PH ? 'Pink' : 'Colourless';
  return { pH, litmus, universal, phenolphthalein };
}

export function stagesFor(reaction: AcidReaction): IndicatorStage[] {
  if (reaction.pattern === 'no-reaction') return ['start'];
  return partnerById(reaction.partner).excessPH === undefined ? ['start', 'used-up'] : ['start', 'used-up', 'excess'];
}

export function stagePH(reaction: AcidReaction, stage: IndicatorStage): number {
  if (stage === 'start' || reaction.pattern === 'no-reaction') return START_PH;
  if (stage === 'used-up') return NEUTRAL_SALT_PH;
  const excess = partnerById(reaction.partner).excessPH;
  if (excess === undefined) throw new Error(`No excess stage for ${reaction.id}`);
  return excess;
}

export function stageExplanation(reaction: AcidReaction, stage: IndicatorStage): string {
  if (reaction.pattern === 'no-reaction') return 'Nothing reacts, so the acid stays acidic (about pH 1).';
  if (stage === 'start') return 'The dilute acid is full of H⁺ ions: about pH 1.';
  if (stage === 'used-up') return 'The H⁺ ions are used up and a salt solution is left: about pH 7 in this model.';
  return `Extra ${partnerById(reaction.partner).name} dissolves and makes the solution alkaline: about pH ${stagePH(reaction, stage)}.`;
}

function capitalise(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
